/**
 * AuthContext — the admin panel's global authentication / authorization state.
 *
 * Holds the currently signed-in user (id, email, role, permissions) and exposes
 * permission-check helpers used throughout the app to gate routes, menu items,
 * and actions. The provider does NOT perform login itself; login screens write
 * the JWT tokens to localStorage, and this context reads them to load the profile.
 *
 * Token storage (localStorage keys, shared with `../api/client`):
 *  - `mstms_access_token`  — JWT access token (30m)
 *  - `mstms_refresh_token` — refresh token (7d)
 *  - `mstms_user`          — cached user blob
 *
 * Wrap the app once near the root in <AuthProvider>, then consume via useAuth().
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { isAxiosError } from 'axios';
import { getMyProfile } from '../api/roles';
import { resetSessionExpiredRedirectFlag } from '../api/client';

/** Must match the apiClient baseURL — reads from the same env var. */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

/** Shape of the authenticated user as returned by the `getMyProfile` API call. */
interface AuthUser {
  userId: string;
  email: string;
  role: string;
  permissions: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  /** Check if user has a specific permission. Super admin always returns true. */
  hasPermission: (permission: string) => boolean;
  /** Check if user has any of the given permissions. */
  hasAnyPermission: (permissions: string[]) => boolean;
  /** Reload user profile and permissions from API. */
  refreshAuth: () => Promise<void>;
  /** Clear auth state (on logout). */
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  refreshAuth: async () => {},
  clearAuth: () => {},
});

/**
 * Provider that loads the current user's profile on mount and supplies auth
 * state + permission helpers to descendants.
 *
 * On mount (and on `refreshAuth`) it reads the access token from localStorage
 * and, if present, fetches the profile via the `getMyProfile` API call. A 401
 * means the session is dead, so the stored tokens are cleared. `loading` is true
 * until the first fetch attempt settles — consumers use it to render a splash
 * before deciding whether to redirect to login.
 *
 * @param children React subtree that will have access to the auth context.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Reads the token, loads the profile, and reconciles state. Memoized so the
  // mount effect and refreshAuth share one stable reference.
  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem('mstms_access_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const profile = await getMyProfile();
      setUser(profile);
      resetSessionExpiredRedirectFlag();
    } catch (e) {
      setUser(null);
      // Only a 401 is treated as an expired/invalid session worth clearing;
      // other errors (network, 5xx) leave the tokens in place for a retry.
      if (isAxiosError(e) && e.response?.status === 401) {
        localStorage.removeItem('mstms_access_token');
        localStorage.removeItem('mstms_refresh_token');
        localStorage.removeItem('mstms_user');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Load the profile once when the provider mounts.
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Re-fetch permissions when the window regains focus.
  useEffect(() => {
    const onFocus = () => {
      const token = localStorage.getItem('mstms_access_token');
      if (token) fetchProfile();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchProfile]);

  // SSE: listen for real-time permission changes pushed from the API.
  // When an admin updates a role, the API pushes 'permissions-changed' to all
  // connected clients with that role — triggering an instant re-fetch.
  const sseRef = useRef<EventSource | null>(null);
  useEffect(() => {
    const token = localStorage.getItem('mstms_access_token');
    if (!token) return;

    const url = `${API_BASE_URL}/auth/events?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    sseRef.current = es;

    es.onmessage = () => {
      fetchProfile();
    };

    es.onerror = () => {
      // Auto-reconnects; no action needed
    };

    return () => {
      es.close();
      sseRef.current = null;
    };
  }, [fetchProfile]);

  // Single-permission check. Note: super_admin bypasses the list and is always
  // granted; otherwise the permission must be in user.permissions.
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user) return false;
      if (user.role === 'super_admin') return true;
      return user.permissions.includes(permission);
    },
    [user],
  );

  // Multi-permission check (OR semantics). super_admin is again always granted.
  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      if (!user) return false;
      if (user.role === 'super_admin') return true;
      return permissions.some((p) => user.permissions.includes(p));
    },
    [user],
  );

  // Re-fetch the profile (e.g. after role/permission changes or token refresh),
  // flipping loading back on for the duration.
  const refreshAuth = useCallback(async () => {
    setLoading(true);
    await fetchProfile();
  }, [fetchProfile]);

  // Local logout: drop the in-memory user and wipe all stored tokens. Does not
  // call the server.
  const clearAuth = useCallback(() => {
    setUser(null);
    localStorage.removeItem('mstms_access_token');
    localStorage.removeItem('mstms_refresh_token');
    localStorage.removeItem('mstms_user');
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, hasPermission, hasAnyPermission, refreshAuth, clearAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to consume the auth context. Use anywhere inside <AuthProvider> to read
 * the current `user`/`loading` state or to gate UI with `hasPermission` /
 * `hasAnyPermission`, e.g. `const { hasPermission } = useAuth()`.
 *
 * @returns The current {@link AuthContextValue}.
 */
export function useAuth() {
  return useContext(AuthContext);
}
